/**
 * @file CreateDesignDialog.tsx
 * @description Окно «Создать дизайн» (phase19.16; решения пользователя
 * 2026-10-12): название, «С чего начать» — пустой шаблон платформы или копия
 * сохранённого дизайна (по умолчанию — тот, что на сайте), новый дизайн
 * сразу открывается в черновике. Неопубликованные правки оформления
 * заменятся — предупреждение и страховочная копия «Черновик 12.10»
 * (включена; не нужна, если вид черновика уже есть среди дизайнов).
 * Перед созданием дожидается автосохранения («Сохраняем правки…»).
 * На телефоне — во весь экран с липким низом.
 */

import { Copy, SquareDashed } from "lucide-react";
import { useEffect, useState } from "react";

import { Button } from "../../ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../../ui/dialog";
import { NoticeStrip } from "../../ui/NoticeStrip";
import { OptionCards } from "../../site-view/OptionCards";
import { TextField } from "../../site-view/TextField";
import type { CreateNewDesignInput } from "../../../hooks/site-builder/useDesigns";
import type { SiteDesignList } from "../../../lib/api-site-designs";
import { DESIGN_CONFLICT_CODES, designErrorCode, designErrorText } from "../../../lib/site-view/design-errors";
import { DESIGN_NAME_MAX, draftCopyBaseName, isDesignNameTaken, sortDesigns, uniqueDesignName } from "../../../lib/site-view/design-status";
import { DesignSourceList } from "./DesignSourceList";
import { SafetyCopyRow, STICKY_FOOTER } from "./SafetyCopyRow";

type StartKind = "empty" | "design";
type Phase = "idle" | "settle" | "create";

const NAME_REQUIRED = "Назовите дизайн — например, «Лето» или «Новогодний».";
const nameTakenText = (name: string) => `Дизайн «${name}» уже есть. Назовите иначе или обновите его из текущего вида в списке.`;

interface Props {
  open: boolean;
  list: SiteDesignList;
  /** Неопубликованных правок оформления в черновике. */
  themeChanges: number;
  onClose: () => void;
  /** Создать и применить; ошибку окно покажет само. */
  onSubmit: (input: CreateNewDesignInput, onSettled: () => void) => Promise<void>;
  /** «Обновить список» после конфликта версий. */
  onReloadAll: () => void;
}

export function CreateDesignDialog({ open, list, themeChanges, onClose, onSubmit, onReloadAll }: Props) {
  const items = sortDesigns(list.items);
  const [name, setName] = useState("");
  const [kind, setKind] = useState<StartKind>("empty");
  const [designId, setDesignId] = useState<string | null>(null);
  const [copyDraft, setCopyDraft] = useState(true);
  const [nameError, setNameError] = useState<string | null>(null);
  const [error, setError] = useState<{ text: string; conflict: boolean } | null>(null);
  const [phase, setPhase] = useState<Phase>("idle");

  useEffect(() => {
    if (!open) return;
    // По умолчанию — копия дизайна, который на сайте; такого нет — пустой шаблон.
    const published = sortDesigns(list.items).find((d) => d.matchesPublished) ?? null;
    setName("");
    setKind(published ? "design" : "empty");
    setDesignId(published?.id ?? list.items[0]?.id ?? null);
    setCopyDraft(true);
    setNameError(null);
    setError(null);
    setPhase("idle");
    // Сброс только при открытии: перечитанный список не сбивает выбор.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Страховочная копия нужна, если правки есть и вида черновика нет среди дизайнов.
  const showCopy = themeChanges > 0 && !list.items.some((d) => d.matchesDraft);
  const copyFull = list.limit.used + 2 > list.limit.max;
  const copyName = uniqueDesignName(list.items, draftCopyBaseName());

  async function submit() {
    const clean = name.trim();
    if (!clean) return setNameError(NAME_REQUIRED);
    if (isDesignNameTaken(list.items, clean)) return setNameError(nameTakenText(clean));
    if (kind === "design" && !designId) return setError({ text: "Выберите дизайн, с которого начать.", conflict: false });
    setNameError(null);
    setError(null);
    setPhase("settle");
    try {
      await onSubmit(
        {
          name: clean,
          from: kind === "design" && designId ? { kind: "design", designId } : { kind: "empty" },
          draftCopyName: showCopy && copyDraft && !copyFull ? copyName : null,
        },
        () => setPhase("create"),
      );
      onClose();
    } catch (err) {
      const code = designErrorCode(err);
      if (code === "site_design_name_taken") setNameError(nameTakenText(clean));
      else setError({ text: designErrorText(err, "Не удалось создать дизайн."), conflict: code !== null && DESIGN_CONFLICT_CODES.has(code) });
    } finally {
      setPhase("idle");
    }
  }

  const options = [
    { value: "empty" as const, label: "Пустой шаблон", hint: "Оформление платформы по умолчанию", preview: <SquareDashed aria-hidden className="size-6 text-on-surface-variant" /> },
    ...(items.length > 0
      ? [{ value: "design" as const, label: "Копия дизайна", hint: "Начать с сохранённого", preview: <Copy aria-hidden className="size-6 text-on-surface-variant" /> }]
      : []),
  ];

  return (
    <Dialog open={open} onOpenChange={(next) => (!next && phase === "idle" ? onClose() : null)}>
      <DialogContent fullScreen className="sm:max-w-[35rem] sm:max-h-[calc(100dvh-2rem)] sm:overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Создать дизайн</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-4">
          {error ? (
            <NoticeStrip
              tone="error"
              action={error.conflict ? <Button variant="outline" onClick={() => { setError(null); onReloadAll(); }}>Обновить список</Button> : undefined}
            >
              {error.text}
            </NoticeStrip>
          ) : null}
          <TextField label="Название" value={name} max={DESIGN_NAME_MAX} placeholder="Осень 2026" error={nameError} onChange={(v) => { setName(v); setNameError(null); }} />
          <div className="flex flex-col gap-2">
            <span className="text-sm font-medium text-on-surface">С чего начать</span>
            <OptionCards aria-label="С чего начать" columns={2} options={options} value={kind} onChange={setKind} />
          </div>
          {kind === "design" ? <DesignSourceList items={items} value={designId} onChange={setDesignId} /> : null}
          <NoticeStrip tone="info">Новый дизайн сразу откроется в черновике. Сайт не изменится, пока вы не нажмёте «Опубликовать».</NoticeStrip>
          {themeChanges > 0 ? (
            <NoticeStrip tone="warning">
              В черновике есть неопубликованные правки оформления ({themeChanges}) — они заменятся новым дизайном.
            </NoticeStrip>
          ) : null}
          {showCopy ? (
            <SafetyCopyRow checked={copyDraft} onChange={setCopyDraft} full={copyFull}>
              {`Сначала сохранить их дизайном «${copyName}»`}
            </SafetyCopyRow>
          ) : null}
        </div>
        <DialogFooter className={STICKY_FOOTER}>
          <Button variant="ghost" disabled={phase !== "idle"} onClick={onClose}>
            Отмена
          </Button>
          <Button loading={phase !== "idle"} onClick={() => void submit()}>
            {phase === "settle" ? "Сохраняем правки…" : "Создать и открыть в черновике"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
